import { jsPDF } from "jspdf";
import {
  TEXT_X_MM, TEXT_WIDTH_MM,
  mmToPt, drawHeader, drawFooter, ensureSpace, getTextRegion, getSignatureLineY, drawRichText,
} from "./referralLayout";
import { renderMarkdown } from "@emr/shared/markdown";
import { fmtDatePdf } from "@emr/shared/india";
import type { LetterType } from "@emr/shared/constants";

// 2026-11-01, Batch 7 Wave 2 dedupe — drawRichText + stripTags were extracted
// verbatim to ./layoutShared.ts (re-exported through this renderer's layout
// sibling, referralLayout) — they were byte-identical (modulo the export
// keyword) with the copies inlined in certificatePdf.ts / patientInfoPdf.ts,
// EXCEPT one drifted constant: the h1 heading size, which is now the h1Size
// parameter (referral passes its previous literal 14; certificate passes 13).

export interface ReferralPdfData {
  letterType: LetterType;
  patientName: string;
  clinicUhid: string | null;
  patientDob: string | null;
  patientGender: "Male" | "Female" | "Other" | null;
  doctorName: string | null;
  doctorGender: "Male" | "Female" | "Other" | null;
  doctorDesignation: string | null;
  doctorClinic: string | null;
  doctorAddress: string | null;
  subject: string | null;
  body: string | null;
  signatoryName: string | null;
  signatoryQualifications: string | null;
  signatoryRegNumber: string | null;
  date: string;
}

export async function createReferralPdf(data: ReferralPdfData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  await drawHeader(doc);
  await drawFooter(doc);

  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(124, 45, 18);
  const title = data.letterType === "Incoming" ? "Referral Reply" : "Referral Letter";
  doc.text(title, x, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 95, 88);
  doc.text(fmtDatePdf(data.date), x + w, y, { align: "right" });
  y += mmToPt(7);

  // Patient info bar
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(26, 20, 19);
  doc.text(`Patient: ${data.patientName}`, x, y);
  if (data.clinicUhid) {
    doc.setFont("helvetica", "normal");
    doc.text(`UHID: ${data.clinicUhid}`, x + w, y, { align: "right" });
  }
  y += mmToPt(5);

  // Recipient/From block
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 50, 45);
  if (data.letterType === "Incoming") {
    doc.text("From:", x, y);
  } else {
    doc.text("To:", x, y);
  }
  doc.setFont("helvetica", "bold");
  doc.text(data.doctorName ?? "", x + mmToPt(12), y);
  y += mmToPt(4.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(107, 95, 88);
  if (data.doctorDesignation) {
    doc.text(data.doctorDesignation, x + mmToPt(12), y);
    y += mmToPt(4);
  }
  if (data.doctorClinic) {
    doc.text(data.doctorClinic, x + mmToPt(12), y);
    y += mmToPt(4);
  }
  if (data.doctorAddress) {
    const addrLines = doc.splitTextToSize(data.doctorAddress, w - mmToPt(14));
    doc.text(addrLines, x + mmToPt(12), y);
    y += mmToPt(4) * addrLines.length;
  }
  y += mmToPt(3);

  // Subject (Referral To only)
  if (data.letterType === "Outgoing" && data.subject) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(26, 20, 19);
    const subjLines = doc.splitTextToSize(`Subject: ${data.subject}`, w);
    y = await ensureSpace(doc, y, mmToPt(5) * subjLines.length);
    doc.text(subjLines, x, y);
    y += mmToPt(5) * subjLines.length;
    y += mmToPt(2);
  }

  // Salutation
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(26, 20, 19);
  const salutation =
    data.letterType === "Incoming"
      ? "Dear Doctor,"
      : data.doctorGender === "Female"
        ? "Dear Dr. (Madam),"
        : data.doctorGender === "Male"
          ? "Dear Dr. (Sir),"
          : "Dear Doctor,";
  y = await ensureSpace(doc, y, mmToPt(6));
  doc.text(salutation, x, y);
  y += mmToPt(7);

  // Body — rendered from markdown to plain text blocks (jsPDF has no HTML renderer;
  // we use the markdown parser to separate headings/paragraphs/lists, then draw as plain text).
  if (data.body) {
    const html = renderMarkdown(data.body);
    y = await drawRichText(doc, y, x, w, html, 14);
    y += mmToPt(3);
  }

  // Sign-off
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  y = await ensureSpace(doc, y, mmToPt(18));
  doc.text("Yours sincerely,", x, y);
  y += mmToPt(14);

  // Signatory block (bottom-right, anchored above the footer)
  const signY = Math.max(y, getSignatureLineY(doc));
  const signX = x + w - mmToPt(60);
  doc.setDrawColor(200, 190, 180);
  doc.line(signX, signY, signX + mmToPt(50), signY);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(26, 20, 19);
  doc.text(data.signatoryName ?? "Doctor", signX + mmToPt(50), signY + mmToPt(4), { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(107, 95, 88);
  if (data.signatoryQualifications) {
    doc.text(data.signatoryQualifications, signX + mmToPt(50), signY + mmToPt(7), { align: "right" });
  }
  if (data.signatoryRegNumber) {
    doc.text(`Reg No: ${data.signatoryRegNumber}`, signX + mmToPt(50), signY + mmToPt(10), { align: "right" });
  }

  return doc;
}
